<template>
  <div
    v-if="setupLoaded"
    :class="['flex min-h-dvh flex-col', !isFramelessView ? 'pb-3' : 'pb-0']">
    <MobileLoyaltyButton v-if="isIframeMode" />
    <Header v-if="!isFramelessView && route.name !== 'playerLogin'" />
    <main :class="['flex flex-grow md:justify-center']">
      <router-view
        v-slot="{ Component }"
        :class="['max-w-full', isFramelessView ? '' : 'md:max-w-[1440px]']">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <Footer
      v-if="!isFramelessView && route.name !== 'playerLogin'"
      class="px-4" />
  </div>

  <InformativeModal
    :isVisible="apiResponseError"
    :title="informativeModalTitle"
    :description="informativeModalDescription"
    :buttonText="informativeModalButtonText"
    :icon="reloadIcon"
    :showBell="true"
    type="danger"
    @update:isVisible="redirectToHome" />

  <!-- Modal for 90-night limit (só renderiza após setup para ter messages do locale carregados) -->
  <InformativeModal
    v-if="setupLoaded"
    :isVisible="bookingQueryStore.showMaxNightsModal"
    :title="$t('general.maximumStayReached')"
    :description="$t('general.maximumStayDescription')"
    :buttonText="$t('general.closeButtonText')"
    type="neutral"
    @update:isVisible="bookingQueryStore.showMaxNightsModal = false" />

  <ShoppingActivationNotifications />
  <ShoppingRecovery />
  <AgenticButtonLauncher v-if="setupLoaded" />
</template>

<script setup>
import {
  onMounted,
  provide,
  ref,
  defineAsyncComponent,
  watch,
  computed,
} from 'vue';
import { RouterView, useRouter, useRoute } from 'vue-router';
import router, { trackInitialPageView } from '@/router';
import { storeToRefs } from 'pinia';

import { useAppConfigStore } from '@stores/appConfig.store';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useGlobalNavigation } from '@/composables/useNavigation';
import { formatDate } from '@composables/useFormattedDate';
import { useAppInitialization } from '@/composables/useAppInitialization';
import { useGeolocation } from '@/composables/useGeolocation';
import { useScreenSize } from '@composables/useScreenSize';
import { applyCapturedHandoffToSearchParams } from '@/services/analytics';
import Header from '@/components/layout/AppHeader.vue';
import Footer from '@/components/layout/AppFooter.vue';
import MobileLoyaltyButton from '@/components/layout/MobileLoyaltyButton.vue';
import AgenticButtonLauncher from '@/components/AgenticButtonLauncher.vue';

const ShoppingActivationNotifications = defineAsyncComponent(
  () =>
    import('@/components/customComponents/ShoppingActivationNotifications.vue'),
);

const InformativeModal = defineAsyncComponent(
  () => import('@/components/customComponents/InformativeModal.vue'),
);

const ShoppingRecovery = defineAsyncComponent(
  () => import('@/components/ShoppingRecovery/ShoppingRecovery.vue'),
);

import useHttp from '@/utils/httpClient';

const reloadIcon = defineAsyncComponent(() => import('@icons/replay.svg'));

const http = useHttp();
const { apiResponseError } = storeToRefs(http);

const appConfigStore = useAppConfigStore();
const bookingQueryStore = useBookingQueryStore();
const route = useRoute();

const isFramelessView = computed(() => {
  if (route.name === 'agent') return true;
  return route.query.iframeMode === '1' || route.query.iframeMode === 'overlay';
});

const isIframeMode = computed(() => {
  if (route.name === 'agent') return false;
  return route.query.iframeMode === '1';
});

const transitionName = ref('slide-left');
let navigationStack = [];
let isNavigatingBack = false;
const navigationRouter = useRouter();
navigationRouter.beforeEach((to, from, next) => {
  const fromPath = from.path;
  const toPath = to.path;

  if (
    navigationStack.length > 0 &&
    navigationStack[navigationStack.length - 1] === toPath
  ) {
    isNavigatingBack = true;
    navigationStack.pop();
  } else {
    isNavigatingBack = false;
    if (fromPath) navigationStack.push(fromPath);
  }
  transitionName.value = isNavigatingBack ? 'slide-left' : 'slide-right';

  next();
});

const { formatCurrency, setCurrency } = appConfigStore;
const { setupLoaded, applicationSetup, setupCurrency } =
  storeToRefs(appConfigStore);

const { userCountryCode, userCurrency } = useGeolocation();

const { goBack } = useGlobalNavigation();
provide('goBack', goBack);
provide('formatCurrency', formatCurrency);
provide('applicationSetup', applicationSetup);
provide('formatDate', formatDate);
provide('userCountryCode', userCountryCode);
provide('userCurrency', userCurrency);

const {
  initializeApp,
  informativeModalTitle,
  informativeModalDescription,
  informativeModalButtonText,
} = useAppInitialization();

// Watch for changes to applicationSetup to track the initial page view
watch(
  () => applicationSetup.value?.hotel_id,
  (hotelId) => {
    if (hotelId) {
      // Track the initial page view once hotel_id is available
      trackInitialPageView(hotelId);
    }
  },
  { immediate: true },
);

// Watch for setupLoaded to show pending modal
watch(setupLoaded, (isLoaded) => {
  if (isLoaded) {
    bookingQueryStore.showPendingMaxNightsModal();
  }
});

onMounted(() => {
  // Mobile channel key only in classic booking engine; skip on frameless views
  if (route.name === 'agent') return;

  // Overlay / iframe is the website widget on desktop. Read iframeMode from
  // location.search — route.query is often still empty on this first mount,
  // which used to miss the skip and reload into the mobile channel (iframe
  // is ~430px wide so useScreenSize reports isMobile). That second load
  // also dropped gc_* after the handoff replaceState.
  const urlParams = new URLSearchParams(window.location.search);
  const iframeMode = urlParams.get('iframeMode');
  const isIframe = iframeMode === '1' || iframeMode === 'overlay';

  if (!isIframe) {
    const { isMobile } = useScreenSize();
    const currentChannelKey = urlParams.get('channelKey');

    if (
      isMobile.value &&
      (!currentChannelKey ||
        currentChannelKey === '58e5de4e971fc00be29aa10492813ad4')
    ) {
      urlParams.set('channelKey', '974d82150b02b308a6a79ba2f87acad6');
      applyCapturedHandoffToSearchParams(urlParams);
      window.location.href = `${window.location.pathname}?${urlParams.toString()}`;
      return;
    }
  }

  Promise.all([initializeApp()])
    .then(() => {
      setCurrency(setupCurrency.value);
    })
    .catch((error) => {
      console.error('Failed to initialize app:', error);
    });
});

const redirectToHome = () => {
  const filteredQuery = {
    [import.meta.env.VITE_APIKEY_PARAM_NAME]:
      route.query[import.meta.env.VITE_APIKEY_PARAM_NAME],
    channelKey: route.query.channelKey,
    language: route.query.language,
    hotel_id: route.query.hotel_id,
    referrer: route.query.referrer,
    url: route.query.url,
  };

  // Preserve iframeMode parameter if it exists
  if (route.query.iframeMode) {
    filteredQuery.iframeMode = route.query.iframeMode;
  }

  router
    .push({
      name: 'home',
      query: filteredQuery,
    })
    .then(() => {
      window.location.reload();
    });
  apiResponseError.value = false;
};
</script>
<style scoped>
main > div {
  transition-property: margin;
  transition-duration: 1s;
}

.slide-right-enter-active,
.slide-left-enter-active {
  transition:
    transform 0.5s ease-in,
    opacity 0.5s ease-in;
}
.slide-right-leave-active,
.slide-left-leave-active {
  transition:
    transform 0.5s ease-out,
    opacity 0.5s ease-out;
}
.slide-right-enter-from,
.slide-left-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-leave-to,
.slide-left-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
